import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const ChatWidget = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isChatEnabled, setIsChatEnabled] = useState(true);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '¡Hola! 👋 Soy el asistente de **REM ENTERPRISES**. ¿En qué puedo ayudarte hoy?'
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL || '/api'}/settings`);
        setIsChatEnabled(response.data.isChatEnabled);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  if (!isChatEnabled) {
    return null;
  }

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    try {
      const response = await axios.post('/api/chat', {
        messages: newMessages
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.reply || 'Lo siento, no pude procesar tu consulta.'
      };

      setMessages([...newMessages, assistantMessage]);
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: 'Lo siento, hubo un error. Por favor intenta de nuevo.'
      };
      setMessages([...newMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatMessage = (content) => {
    // Regex para detectar imágenes en formato markdown ![alt](url|id)
    const markdownImageRegex = /!\[([^\]]*)\]\(([^\|\)]+)(?:\|([^\)]+))?\)/g;
    // Regex para URLs directas de imágenes
    const directImageRegex = /(https?:\/\/[^\s\)]+\.(jpg|jpeg|png|gif|webp|svg)[^\s\)]*)/gi;
    // Regex para enlaces clickeables
    const linkRegex = /(https?:\/\/[^\s]+)/gi;

    // Dividir el contenido en líneas para procesamiento
    const lines = content.split('\n');
    const processedLines = [];

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i].trim();
      let hasProcessedContent = false;

      // 1. Procesar imágenes en formato markdown ![alt](url)
      const markdownMatches = [...line.matchAll(markdownImageRegex)];
      if (markdownMatches.length > 0) {
        let lastIndex = 0;

        markdownMatches.forEach((match, imgIndex) => {
          const [fullMatch, altText, imageUrl, productId] = match;
          const matchStart = match.index;

          // Agregar texto antes de la imagen
          if (matchStart > lastIndex) {
            const beforeText = line.substring(lastIndex, matchStart);
            if (beforeText.trim()) {
              processedLines.push(
                <div key={`text-${i}-${imgIndex}-before`} dangerouslySetInnerHTML={{
                  __html: beforeText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                }} />
              );
            }
          }

          // Agregar la imagen con botones
          processedLines.push(
            <div key={`img-${i}-${imgIndex}`} className="product-card-chat">
              <img
                src={imageUrl}
                alt={altText || "Imagen del producto"}
                className="product-image-chat"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="image-error" style={{ display: 'none' }}>
                📷 Imagen no disponible
              </div>
              <div className="product-actions">
                <button
                  className="btn btn-sm btn-success me-1"
                  onClick={() => window.open(imageUrl, '_blank')}
                >
                  <i className="fas fa-external-link-alt"></i> Ver imagen
                </button>
                <button
                  className="btn btn-sm btn-primary me-1"
                  onClick={() => {
                    if (productId && productId.trim()) {
                      const url = `/product/${productId.trim()}`;
                      navigate(url);
                    } else {
                      const productName = altText || 'producto';
                      setInput(`buscar ${productName}`);
                    }
                  }}
                >
                  <i className="fas fa-shopping-bag"></i> Ver producto
                </button>
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={() => {
                    const productName = altText || 'producto';
                    setInput(`más información sobre ${productName}`);
                  }}
                >
                  <i className="fas fa-info-circle"></i> Más info
                </button>
              </div>
            </div>
          );

          lastIndex = matchStart + fullMatch.length;
          hasProcessedContent = true;
        });

        // Agregar texto después de todas las imágenes
        if (lastIndex < line.length) {
          const afterText = line.substring(lastIndex);
          if (afterText.trim()) {
            processedLines.push(
              <div key={`text-${i}-after`} dangerouslySetInnerHTML={{
                __html: afterText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
              }} />
            );
          }
        }
      }

      // 2. Si no hay imágenes markdown, buscar URLs directas de imágenes
      else if (directImageRegex.test(line)) {
        const imageMatches = line.match(directImageRegex);
        let remainingText = line;

        imageMatches.forEach((imageUrl, imgIndex) => {
          const cleanUrl = imageUrl.replace(/[\)\]]+$/, '');
          const parts = remainingText.split(imageUrl);

          if (parts[0] && parts[0].trim()) {
            processedLines.push(
              <div key={`text-${i}-${imgIndex}-before`} dangerouslySetInnerHTML={{
                __html: parts[0].replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
              }} />
            );
          }

          processedLines.push(
            <div key={`img-${i}-${imgIndex}`} className="product-card-chat">
              <img
                src={cleanUrl}
                alt="Imagen del producto"
                className="product-image-chat"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="image-error" style={{ display: 'none' }}>
                📷 Imagen no disponible
              </div>
              <div className="product-actions">
                <button
                  className="btn btn-sm btn-success me-1"
                  onClick={() => window.open(cleanUrl, '_blank')}
                >
                  <i className="fas fa-external-link-alt"></i> Ver imagen
                </button>
                <button
                  className="btn btn-sm btn-primary me-1"
                  onClick={() => {
                    navigate('/tienda');
                  }}
                >
                  <i className="fas fa-shopping-bag"></i> Ver producto
                </button>
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={() => {
                    setInput('más información sobre este producto');
                  }}
                >
                  <i className="fas fa-info-circle"></i> Más info
                </button>
              </div>
            </div>
          );

          remainingText = parts.slice(1).join(imageUrl);
        });

        if (remainingText && remainingText.trim()) {
          processedLines.push(
            <div key={`text-${i}-after`} dangerouslySetInnerHTML={{
              __html: remainingText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            }} />
          );
        }
        hasProcessedContent = true;
      }

      // 3. Si no hay imágenes, procesar enlaces clickeables y texto normal
      if (!hasProcessedContent) {
        // Detectar enlaces y hacerlos clickeables
        if (linkRegex.test(line)) {
          const parts = line.split(linkRegex);
          const elements = [];

          for (let j = 0; j < parts.length; j++) {
            if (j % 2 === 0) {
              // Texto normal
              if (parts[j]) {
                const processedText = parts[j]
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\*(.*?)\*/g, '<em>$1</em>')
                  .replace(/_(.*?)_/g, '<em>$1</em>')
                  .replace(/~~(.*?)~~/g, '<del>$1</del>');
                elements.push(<span key={`text-${j}`} dangerouslySetInnerHTML={{ __html: processedText }} />);
              }
            } else {
              // Enlace clickeable
              elements.push(
                <button
                  key={`link-${j}`}
                  className="btn btn-sm btn-link p-0 mx-1"
                  onClick={() => window.open(parts[j], '_blank')}
                  style={{ textDecoration: 'underline', color: '#1f8e38' }}
                >
                  <i className="fas fa-external-link-alt me-1"></i>
                  {parts[j].length > 30 ? parts[j].substring(0, 30) + '...' : parts[j]}
                </button>
              );
            }
          }

          processedLines.push(
            <div key={`text-${i}`}>{elements}</div>
          );
        } else {
          // Texto normal sin enlaces
          const processedLine = line
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/_(.*?)_/g, '<em>$1</em>')
            .replace(/~~(.*?)~~/g, '<del>$1</del>');

          if (processedLine.trim()) {
            processedLines.push(
              <div key={`text-${i}`} dangerouslySetInnerHTML={{ __html: processedLine }} />
            );
          }
        }
      }
    }

    return processedLines;
  };

  const quickActions = [
    { text: "Ver productos", action: "buscar productos" },
    { text: "Categorías", action: "mostrar categorías" },
    { text: "Mi pedido", action: "consultar mi pedido" }
  ];

  const handleQuickAction = (action) => {
    setInput(action);
  };

  return (
    <div className={`chat-widget ${isOpen ? 'open' : 'closed'}`}>
      {/* Botón flotante */}
      <button
        className="chat-toggle"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Abrir chat"
      >
        {isOpen ? (
          <i className="fas fa-times"></i>
        ) : (
          <>
            <i className="fas fa-comments"></i>
            <span className="chat-badge">💬</span>
          </>
        )}
      </button>

      {/* Ventana de chat */}
      {isOpen && (
        <div className="chat-container">
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-info">
              <div className="chat-avatar">
                <i className="fa-brands fa-reddit-alien"></i>
              </div>
              <div>
                <h6 className="mb-0">Asistente REM IA</h6>
                <small>En línea</small>
              </div>
            </div>
            <button
              className="btn btn-sm btn-outline-light"
              onClick={() => setIsOpen(false)}
            >
              <i className="fas fa-minus"></i>
            </button>
          </div>

          {/* Mensajes */}
          <div className="chat-messages">
            {messages.map((message, index) => (
              <div key={index} className={`message ${message.role}`}>
                <div className="message-content">
                  {formatMessage(message.content)}
                </div>
                <div className="message-time">
                  {new Date().toLocaleTimeString('es-UY', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="message assistant">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Acciones rápidas */}
          {messages.length <= 1 && (
            <div className="quick-actions">
              <small className="text-muted mb-2 d-block">Acciones rápidas:</small>
              <div className="d-flex flex-wrap gap-1">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => handleQuickAction(action.action)}
                  >
                    {action.text}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <form className="chat-input-form" onSubmit={sendMessage}>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Escribe tu mensaje..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={isLoading}
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!input.trim() || isLoading}
              >
                {isLoading ? (
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Enviando...</span>
                  </div>
                ) : (
                  <i className="fas fa-paper-plane"></i>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
